import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, File as FileIcon, History, Image as ImageIcon, Link2, Trash2, UploadCloud } from 'lucide-react';
import client, { filesAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { useProject } from '../../layout/ProjectLayout';
import { fmtDateTime } from '../../components/ui';

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

async function downloadBlob(fileId, name, version = '') {
  const { data } = await client.get(`/files/${fileId}/download`, {
    params: version ? { version } : {},
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function FilesScreen() {
  const { project } = useProject();
  const [files, setFiles] = useState([]);
  const [quota, setQuota] = useState({ quota_mb: 0, used_bytes: 0 });
  const [progress, setProgress] = useState(null);
  const [showVersions, setShowVersions] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);
  const user = getUser();
  const readOnly = user?.role === 'guest';

  const load = useCallback(async () => {
    const { data } = await filesAPI.list(project.id);
    setFiles(data.files);
    setQuota({ quota_mb: data.quota_mb, used_bytes: data.used_bytes });
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (picked) => {
    for (const f of picked) {
      setProgress({ name: f.name, pct: 0 });
      try {
        await filesAPI.upload(project.id, f, '', (ev) =>
          setProgress({ name: f.name, pct: Math.round((ev.loaded / (ev.total || 1)) * 100) })
        );
      } catch (err) {
        window.alert(err.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setProgress(null);
    await load();
  };

  const share = async (f) => {
    const { data } = await filesAPI.share(f.id);
    if (data.public_token) {
      const url = `https://pms.carlosartiles.com/api/public/files/${data.public_token}`;
      await navigator.clipboard?.writeText(url);
      window.alert(`Public link copied:\n${url}`);
    } else {
      window.alert('Public link disabled.');
    }
    await load();
  };

  const previewFile = async (f) => {
    const { data } = await client.get(`/files/${f.id}/download`, { responseType: 'blob' });
    setPreview({ name: f.name, url: URL.createObjectURL(data) });
  };

  const usedPct = quota.quota_mb ? Math.round((quota.used_bytes / (quota.quota_mb * 1048576)) * 100) : 0;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center gap-3">
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md bg-pm-accent px-3 py-2 text-sm font-semibold text-pm-accent-ink"
            >
              <UploadCloud size={15} />
              Upload files
            </button>
            <input ref={inputRef} type="file" multiple hidden onChange={(e) => e.target.files.length && upload([...e.target.files])} />
          </>
        )}
        <span className="ml-auto text-xs text-pm-faint">
          {fmtSize(quota.used_bytes)} of {quota.quota_mb / 1024} GB used ({usedPct}%)
        </span>
      </div>

      {progress && (
        <div className="mb-3 rounded-md border border-pm-border bg-pm-surface p-2 text-xs">
          Uploading {progress.name}…
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-pm-raised">
            <div className="h-full bg-pm-accent" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      )}

      <ul className="divide-y divide-pm-border/60 rounded-lg border border-pm-border bg-pm-surface">
        {files.map((f) => {
          const isImage = (f.mime || '').startsWith('image/');
          return (
            <li key={f.id} className="group">
              <div className="flex items-center gap-3 px-3 py-2.5">
                {isImage ? <ImageIcon size={16} className="shrink-0 text-pm-muted" /> : <FileIcon size={16} className="shrink-0 text-pm-muted" />}
                <button
                  type="button"
                  onClick={() => (isImage ? previewFile(f) : downloadBlob(f.id, f.name))}
                  className="min-w-0 flex-1 truncate text-left text-sm hover:text-pm-accent"
                >
                  {f.name}
                </button>
                {f.public_token && <Link2 size={12} className="text-pm-accent" title="Has public link" />}
                <span className="text-xs text-pm-faint">{fmtSize(f.size)}</span>
                <span className="hidden text-xs text-pm-faint sm:inline">{fmtDateTime(f.updated_at)}</span>
                {(f.versions || []).length > 1 && (
                  <button type="button" onClick={() => setShowVersions(showVersions === f.id ? null : f.id)} className="rounded p-1 text-pm-faint hover:text-pm-text" title={`${f.versions.length} versions`}>
                    <History size={13} />
                  </button>
                )}
                <button type="button" onClick={() => downloadBlob(f.id, f.name)} className="rounded p-1 text-pm-faint hover:text-pm-text" title="Download">
                  <Download size={13} />
                </button>
                {!readOnly && (
                  <>
                    <button type="button" onClick={() => share(f)} className="rounded p-1 text-pm-faint hover:text-pm-text" title={f.public_token ? 'Disable public link' : 'Share public link'}>
                      <Link2 size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`Delete "${f.name}" and its versions?`)) return;
                        await filesAPI.remove(f.id);
                        await load();
                      }}
                      className="rounded p-1 text-pm-faint hover:text-pm-danger"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
              {showVersions === f.id && (
                <ul className="border-t border-pm-border/40 bg-pm-bg/40 px-10 py-1.5">
                  {[...(f.versions || [])].reverse().map((v, i) => (
                    <li key={v.path} className="flex items-center gap-3 py-1 text-xs text-pm-muted">
                      <span>v{f.versions.length - i}</span>
                      <span>{fmtSize(v.size)}</span>
                      <span>{v.uploaded_by}</span>
                      <span className="text-pm-faint">{fmtDateTime(v.at)}</span>
                      <button type="button" onClick={() => downloadBlob(f.id, f.name, v.path)} className="ml-auto text-pm-faint hover:text-pm-text" title="Download this version">
                        <Download size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
        {files.length === 0 && !progress && (
          <li className="px-3 py-12 text-center text-sm text-pm-muted">No files yet{readOnly ? '.' : ' — upload the first one.'}</li>
        )}
      </ul>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onMouseDown={(e) => e.target === e.currentTarget && setPreview(null)} role="dialog">
          <div className="max-h-full max-w-3xl overflow-auto rounded-lg border border-pm-border bg-pm-surface p-3">
            <div className="mb-2 text-xs text-pm-muted">{preview.name}</div>
            <img src={preview.url} alt={preview.name} className="max-h-[80vh] max-w-full rounded" />
          </div>
        </div>
      )}
    </div>
  );
}
